import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { ThemeClasses } from "../../../types/components";
import type { WorkshopScheduleConfig } from "../types";

// Workshop agenda settings: operating hours, slot granularity, bay capacity,
// default service duration and working days. Drives availability + over-booking
// checks for the whole agenda.
interface ScheduleConfigModalProps {
  config: WorkshopScheduleConfig;
  themeClasses: ThemeClasses;
  onCancel: () => void;
  onSubmit: (config: Partial<WorkshopScheduleConfig>) => Promise<void>;
}

const DAY_KEYS = [
  "agenda.sun",
  "agenda.mon",
  "agenda.tue",
  "agenda.wed",
  "agenda.thu",
  "agenda.fri",
  "agenda.sat",
];

export default function ScheduleConfigModal({
  config,
  themeClasses,
  onCancel,
  onSubmit,
}: ScheduleConfigModalProps) {
  const { t } = useTranslation("common");
  const [openTime, setOpenTime] = useState(config.openTime);
  const [closeTime, setCloseTime] = useState(config.closeTime);
  const [slotMinutes, setSlotMinutes] = useState(config.slotMinutes);
  const [bays, setBays] = useState(config.bays);
  const [defaultDurationMinutes, setDefaultDurationMinutes] = useState(
    config.defaultDurationMinutes
  );
  const [workDays, setWorkDays] = useState<number[]>(config.workDays ?? []);
  const [submitting, setSubmitting] = useState(false);

  const inputBase =
    "w-full rounded-lg border px-3 py-2 text-sm outline-none transition-colors bg-app border-line text-content focus:border-primary-500";

  const toggleDay = (d: number) =>
    setWorkDays((prev) =>
      prev.includes(d)
        ? prev.filter((x) => x !== d)
        : [...prev, d].sort((a, b) => a - b)
    );

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await onSubmit({
        openTime,
        closeTime,
        slotMinutes: Number(slotMinutes) || 30,
        bays: Number(bays) || 1,
        defaultDurationMinutes: Number(defaultDurationMinutes) || 60,
        workDays,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className={`mb-1 block text-xs font-medium ${themeClasses.textSecondary}`}>
            {t("mantenimiento.agenda.openTime")}
          </label>
          <input
            type="time"
            value={openTime}
            onChange={(e) => setOpenTime(e.target.value)}
            className={inputBase}
          />
        </div>
        <div>
          <label className={`mb-1 block text-xs font-medium ${themeClasses.textSecondary}`}>
            {t("mantenimiento.agenda.closeTime")}
          </label>
          <input
            type="time"
            value={closeTime}
            onChange={(e) => setCloseTime(e.target.value)}
            className={inputBase}
          />
        </div>
        <div>
          <label className={`mb-1 block text-xs font-medium ${themeClasses.textSecondary}`}>
            {t("mantenimiento.agenda.bays")}
          </label>
          <input
            type="number"
            min={1}
            value={bays}
            onChange={(e) => setBays(Number(e.target.value) || 1)}
            className={inputBase}
          />
        </div>
        <div>
          <label className={`mb-1 block text-xs font-medium ${themeClasses.textSecondary}`}>
            {t("mantenimiento.agenda.slotMinutes")}
          </label>
          <input
            type="number"
            min={5}
            step={5}
            value={slotMinutes}
            onChange={(e) => setSlotMinutes(Number(e.target.value) || 30)}
            className={inputBase}
          />
        </div>
        <div className="col-span-2">
          <label className={`mb-1 block text-xs font-medium ${themeClasses.textSecondary}`}>
            {t("mantenimiento.agenda.defaultDuration")} (min)
          </label>
          <input
            type="number"
            min={5}
            step={5}
            value={defaultDurationMinutes}
            onChange={(e) =>
              setDefaultDurationMinutes(Number(e.target.value) || 60)
            }
            className={inputBase}
          />
        </div>
      </div>

      <div>
        <label className={`mb-1 block text-xs font-medium ${themeClasses.textSecondary}`}>
          {t("mantenimiento.agenda.workDays")}
        </label>
        <div className="flex flex-wrap gap-2">
          {DAY_KEYS.map((k, idx) => {
            const on = workDays.includes(idx);
            return (
              <button
                type="button"
                key={k}
                onClick={() => toggleDay(idx)}
                className={`rounded-lg border px-3 py-1.5 text-xs transition-colors ${
                  on
                    ? "border-primary-500 bg-primary-500 text-white"
                    : `${themeClasses.border} ${themeClasses.text}`
                }`}
              >
                {t(`mantenimiento.${k}`)}
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <button
          onClick={onCancel}
          className={`rounded-lg border px-4 py-2 text-sm ${themeClasses.border} ${themeClasses.buttonText} ${themeClasses.buttonHover}`}
        >
          {t("mantenimiento.cancel")}
        </button>
        <button
          onClick={handleSubmit}
          disabled={submitting}
          className="rounded-lg bg-primary-500 px-4 py-2 text-sm text-on-primary transition-colors hover:bg-primary-400 disabled:opacity-60"
        >
          {submitting
            ? t("mantenimiento.loading")
            : t("mantenimiento.agenda.saveConfig")}
        </button>
      </div>
    </div>
  );
}
